import styled from '@emotion/styled';
import { TitleLarge, SubtitleMedium } from '../components/Typography';
import { colors, typography } from '../theme/tokens';
import Button from '../components/Button';
import { Plus, ArrowSquareOut, MagnifyingGlass, Sparkle } from '@phosphor-icons/react';
import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';
import { useNavigate } from 'react-router-dom';

const RecipesContainer = styled.div`
  min-height: 100vh;
  padding: 1rem;
  overflow-y: auto;
`;

const RecipesHeader = styled.div`
  background-color: ${colors.neutral.white};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const TitleSection = styled.div``;

const RecipesTitle = styled(TitleLarge)`
  color: ${colors.brand.laurel};
  margin-bottom: 1rem;
`;

const TableContainer = styled.div`
  background-color: ${colors.neutral.white};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem 2rem;
  border-bottom: 1px solid ${colors.neutral.darkCharcoal};
  color: ${colors.neutral.darkCharcoal};
  font-family: ${typography.label.large.fontFamily};
  font-size: ${typography.label.large.fontSize};
  line-height: ${typography.label.large.lineHeight};
  background-color: ${colors.neutral.lightCharcoal};
`;

const Td = styled.td`
  padding: 1rem 2rem;
  border-bottom: 1px solid ${colors.neutral.offWhite};
  color: ${colors.neutral.darkCharcoal};
  font-family: 'Gabarito', sans-serif;
  font-size: 14px;
`;

const RecipeImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 4px;
  object-fit: cover;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${colors.brand.laurel};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  transition: all 0.2s;
  font-family: 'Gabarito', sans-serif;
  font-size: 14px;

  &:hover {
    background-color: ${colors.neutral.offWhite};
  }
`;

const StatusLabel = styled.span<{ isPublished: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-family: 'Gabarito', sans-serif;
  font-weight: 500;
  background-color: ${props => props.isPublished ? colors.brand.laurel : colors.neutral.offWhite};
  color: ${props => props.isPublished ? colors.neutral.white : colors.neutral.darkCharcoal};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${colors.neutral.darkCharcoal};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${colors.reds.bright};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${colors.neutral.darkCharcoal};
`;

const LoadMoreButton = styled(Button)`
  margin: 2rem auto;
  display: block;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-top: 1rem;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;

  input {
    width: 100%;
    padding: 0.75rem 2.5rem 0.75rem 0.75rem;
    border: 1px solid ${colors.neutral.lightCharcoal};
    border-radius: 4px;
    font-size: 1rem;
    margin-bottom:0;
    background-color: ${colors.neutral.white};
    color: ${colors.neutral.black};
    
    &:focus {
      outline: none;
      border-color: ${colors.brand.laurel};
    }
  }

  svg {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: ${colors.neutral.lightCharcoal};
    width: 20px;
    height: 20px;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  input[type="checkbox"] {
    width: 24px;
    height: 24px;
    accent-color: ${colors.brand.laurel};
  }
  
  label {
    font-size: 0.875rem;
    color: ${colors.neutral.darkCharcoal};
    cursor: pointer;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
`;

const AIButton = styled(Button)`
  position: relative;
  background: ${colors.brand.darkLaurel} !important;
  color: ${colors.neutral.white} !important;
  
  &:hover {
    background: ${colors.brand.laurel} !important;
    color: ${colors.neutral.white} !important;
  }
  
  &::before,
  &::after {
    content: '✨';
    position: absolute;
    font-size: 12px;
    opacity: 0;
    pointer-events: none;
    animation: sparkle 1.2s ease-in-out infinite;
    animation-delay: 5s;
  }
  
  &::before {
    top: -8px;
    left: 10px;
    animation-delay: 5s;
  }
  
  &::after {
    bottom: -8px;
    right: 10px;
    animation-delay: 5.3s;
  }
  
  svg {
    animation: pulse 1.2s ease-in-out infinite;
    animation-delay: 5s;
  }
  
  @keyframes sparkle {
    0%, 100% { 
      opacity: 0; 
      transform: scale(0) rotate(0deg); 
    }
    50% { 
      opacity: 1; 
      transform: scale(1.2) rotate(180deg); 
    }
  }
  
  @keyframes pulse {
    0%, 100% { 
      transform: scale(1); 
      filter: brightness(1);
    }
    50% { 
      transform: scale(1.1); 
      filter: brightness(1.3);
    }
  }
`;

interface Recipe {
  RecipeId: number;
  Name: string;
  ImageURL: string;
  DateCreated: string;
  Publish: boolean;
}

const Recipes = () => {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFreeOnly, setShowFreeOnly] = useState(false);
  const pageSize = 20;

  const fetchRecipes = async (pageNum: number = 1, append: boolean = false) => {
    try {
      if (pageNum === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);
      
      let query = supabase
        .from('Recipes')
        .select('RecipeId, Name, ImageURL, DateCreated, Publish')
        .order('DateCreated', { ascending: false });

      if (searchQuery) {
        query = query.ilike('Name', `%${searchQuery}%`);
      }

      if (showFreeOnly) {
        query = query.eq('Free', true);
      }

      const { data, error } = await query
        .range((pageNum - 1) * pageSize, pageNum * pageSize - 1);

      if (error) {
        throw error;
      }

      if (data) {
        if (append) {
          setRecipes(prev => [...prev, ...data]);
        } else {
          setRecipes(data);
        }
        setHasMore(data.length === pageSize);
      }
    } catch (err) {
      console.error('Error fetching recipes:', err);
      setError('Failed to load recipes. Please try again.');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchRecipes(1);
  }, [searchQuery, showFreeOnly]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchRecipes(nextPage, true);
  };

  return (
    <RecipesContainer>
      <RecipesHeader>
        <HeaderContent>
          <TitleSection>
            <RecipesTitle>Recipes</RecipesTitle>
            <SubtitleMedium>Manage your recipes</SubtitleMedium>
            <FiltersContainer>
              <SearchInput>
                <input
                  type="text"
                  placeholder="Search recipes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <MagnifyingGlass size={16} />
              </SearchInput>
              <CheckboxContainer>
                <input
                  type="checkbox"
                  id="free-only"
                  checked={showFreeOnly}
                  onChange={(e) => setShowFreeOnly(e.target.checked)}
                />
                <label htmlFor="free-only">Free only</label>
              </CheckboxContainer>
            </FiltersContainer>
          </TitleSection>
          <ActionButtons>
            <Button 
              icon={<Plus />}
              onClick={() => navigate('/recipes/create')}
            >
              Create Recipe
            </Button>
            <AIButton 
              icon={<Sparkle weight="fill" />}
              onClick={() => navigate('/recipes/create-ai')}
            >
              Create with AI Assistant
            </AIButton>
          </ActionButtons>
        </HeaderContent>
      </RecipesHeader>

      <TableContainer>
        {isLoading ? (
          <LoadingMessage>Loading recipes...</LoadingMessage>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : recipes.length === 0 ? (
          <EmptyState>No recipes found. Create your first recipe!</EmptyState>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Image</Th>
                  <Th>Recipe Name</Th>
                  <Th>Date Created</Th>
                  <Th>Published</Th>
                  <Th>Action</Th>
                </tr>
              </thead>
              <tbody>
                {recipes.map((recipe) => (
                  <tr key={recipe.RecipeId}>
                    <Td>
                      <RecipeImage src={recipe.ImageURL} alt={recipe.Name} />
                    </Td>
                    <Td>{recipe.Name}</Td>
                    <Td>{new Date(recipe.DateCreated).toLocaleDateString()}</Td>
                    <Td>
                      <StatusLabel isPublished={recipe.Publish}>
                        {recipe.Publish ? 'LIVE' : 'DRAFT'}
                      </StatusLabel>
                    </Td>
                    <Td>
                      <ActionButton onClick={() => navigate(`/recipes/${recipe.RecipeId}`)}>
                        View
                        <ArrowSquareOut size={20} />
                      </ActionButton>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {hasMore && (
              <LoadMoreButton
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? 'Loading...' : 'Load More'}
              </LoadMoreButton>
            )}
          </>
        )}
      </TableContainer>
    </RecipesContainer>
  );
};

export default Recipes; 